/**
 * One agent's configuration: /agents/teams/new and /agents/teams/:teamId (agents-v2).
 *
 * An agent is a team (src/services/agents/teams.py): steps, each its own definition with a
 * prompt, model, budget, what starts it, its tools and its typed output; and hand-offs
 * between steps, which route on that output and can wait for a person's approval in Slack.
 * The form edits the same document PUT /api/v1/agent-teams takes; datahub checks it.
 *
 * Every save is a version (ENG-378), and none goes live until "Make live". The dropdown picks
 * which version is shown, edited and tested; saving over a version that isn't live can rewrite
 * it in place instead.
 */
import { useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import ApiService from '@/services/api.service';
import { useApiAuthReady } from '@/hooks/useApiAuthReady';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { showToast } from '@/services/toast.service';
import AgentKnowledge from './AgentKnowledge';
import AgentRuns, { WorkItemSheet } from './AgentRuns';
import AgentTest from './AgentTest';
import StepsGraph, { F } from './StepsGraph';
import { AGENTS_PATH, agentUserId, errorText, formatDateTime, teamPath, useConnections, useTeamVersion, useTeamVersions, useTeams } from './data';
import PrincipalPermissions from '@/pages/security/PrincipalPermissions';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CONNECTORS, PLIABLE, cleaned, triggerLabel, emptyMember, type Connection, type TeamDoc } from './catalog';

const api = () => ApiService.getInstance();

const BLANK: TeamDoc = { name: '', description: '', members: [emptyMember(1)], edges: [] };

// Behind agents-v2, like every Agents screen but the overview (AgentsLayout).
export default function AgentTeamEditor() {
  // Keyed by agent: moving to another agent starts over (its version, no draft), not with this one's.
  const { teamId } = useParams();
  return <Editor key={teamId ?? 'new'} />;
}

const TABS = [
  { key: 'general', label: 'General' },
  { key: 'steps', label: 'Steps' },
  { key: 'knowledge', label: 'Knowledge' },
  { key: 'runs', label: 'Runs' },
  { key: 'permissions', label: 'Data Permissions' },
];

function General({ doc, setDoc, connections }: { doc: TeamDoc; setDoc: (d: TeamDoc) => void; connections: Connection[] }) {
  const starts = doc.members.flatMap((m) =>
    m.triggers.map((t) => `${m.name}: ${triggerLabel(t)}`),
  );
  const used = [...new Set(doc.members.flatMap((m) => m.capabilities.map((c) => c.connection)))];
  return (
    <div className="space-y-6 max-w-3xl">
      <F label="Name">
        <Input value={doc.name} placeholder="Ops alert triage" onChange={(e) => setDoc({ ...doc, name: e.target.value })} />
      </F>
      <F label="What it does">
        <Textarea value={doc.description} className="min-h-[100px]" onChange={(e) => setDoc({ ...doc, description: e.target.value })} />
      </F>
      <div className="grid grid-cols-3 gap-4 text-sm">
        <div>
          <h4 className="text-xs text-muted-foreground mb-1">Steps</h4>
          {doc.members.map((m) => (
            <div key={m.id ?? m.name}>{m.name}</div>
          ))}
        </div>
        <div>
          <h4 className="text-xs text-muted-foreground mb-1">Starts on</h4>
          {starts.length ? starts.map((s) => <div key={s}>{s}</div>) : <div className="text-muted-foreground">Nothing yet (Steps → Starts when)</div>}
        </div>
        <div>
          <h4 className="text-xs text-muted-foreground mb-1">Works through</h4>
          {used.length ? (
            used.map((name) => {
              const c = connections.find((x) => x.name === name);
              return <div key={name}>{name === PLIABLE ? 'Pliable' : c ? `${name} (${CONNECTORS[c.connector]?.label ?? c.connector})` : `${name} (missing)`}</div>;
            })
          ) : (
            <div className="text-muted-foreground">No tools yet</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Editor() {
  const { teamId, tab: tabParam } = useParams();
  const isNew = !teamId;
  const navigate = useNavigate();
  const { hash } = useLocation();
  const apiReady = useApiAuthReady();
  const queryClient = useQueryClient();
  // Edits, over the saved agent (or a blank one), shared by General and Steps until saved.
  const [draft, setDoc] = useState<TeamDoc | null>(null);
  const [newTab, setNewTab] = useState('general');
  const [testing, setTesting] = useState(false);
  // The Work Item being watched live (a test's, or one opened from Runs), in the URL so it survives a reload.
  const [search, setSearch] = useSearchParams();
  const watching = search.get('watch');
  const tab = isNew ? newTab : (tabParam ?? 'general');

  const teams = useTeams();
  const connections = useConnections();
  const live = isNew ? null : (teams.data?.teams.find((t) => t.id === teamId) ?? null);
  const waiting = (teamId && teams.data?.waiting?.[teamId]) || 0;
  const versions = useTeamVersions(teamId);
  // The version shown: the one picked, else the live one, else the newest. None for an agent saved before versions.
  const [picked, setPicked] = useState<number | null>(null);
  const selected = picked ?? live?.live_version ?? versions.data?.versions[0]?.number ?? null;
  const isLiveVersion = selected === null || selected === live?.live_version;
  const version = useTeamVersion(teamId, isNew ? null : selected);
  const saved = isNew ? BLANK : selected === null ? live : (version.data ?? null);
  const doc = draft ?? saved;
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(saved);
  const [choosing, setChoosing] = useState(false); // saving over a version that isn't live: new, or in place?

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['agent-teams'] });
  const save = useMutation({
    mutationFn: async (d: TeamDoc) =>
      (await api().request('PUT', '/agent-teams?live=false', cleaned({ ...d, id: teamId }))) as TeamDoc & { saved_version: number },
    onSuccess: (result, d) => {
      // What was just saved is that version: show it at once, rather than the version before it while it loads.
      queryClient.setQueryData(['agent-teams', result.id, 'versions', result.saved_version], { ...d, id: result.id });
      refresh();
      setDoc(null);
      setPicked(result.saved_version);
      showToast.success(`Saved as version ${result.saved_version}. Make it live when it's ready.`);
      if (isNew) navigate(teamPath(result.id!, 'steps'), { replace: true });
    },
    onError: (e) => showToast.error(errorText(e, 'Could not save.')),
  });
  const update = useMutation({
    mutationFn: async (d: TeamDoc) => api().request('PUT', `/agent-teams/${teamId}/versions/${selected}`, cleaned(d)),
    onSuccess: () => {
      refresh();
      setDoc(null);
      showToast.success(`Version ${selected} updated`);
    },
    onError: (e) => showToast.error(errorText(e, 'Could not save.')),
  });
  const makeLive = useMutation({
    mutationFn: async () => api().request('POST', `/agent-teams/${teamId}/versions/${selected}/live`),
    onSuccess: () => {
      refresh();
      showToast.success(`Version ${selected} is live`);
    },
    onError: (e) => showToast.error(errorText(e, 'Could not make it live.')),
  });
  const onSave = () => (isNew || isLiveVersion ? save.mutate(doc!) : setChoosing(true));
  const pick = (n: number) => {
    if (dirty && !window.confirm('Discard your unsaved changes?')) return;
    setDoc(null);
    setPicked(n);
  };
  const remove = useMutation({
    mutationFn: async () => api().request('DELETE', `/agent-teams/${teamId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-teams'] });
      navigate(AGENTS_PATH);
    },
    onError: (e) => showToast.error(errorText(e, 'Could not delete.')),
  });

  if (!isNew && (teams.isLoading || versions.isLoading || version.isLoading || !apiReady))
    return <p className="p-8 text-sm text-muted-foreground">Loading…</p>;
  if (!doc) return <p className="p-8 text-sm text-destructive">Agent not found.</p>;

  const conns = connections.data?.connections ?? [];
  const editing = tab === 'general' || tab === 'steps';

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="px-6 pt-4 page-header-surface flex-shrink-0 space-y-3">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold truncate">{doc.name || 'New agent'}</h1>
          {!isNew && !!versions.data?.versions.length && (
            <Select value={String(selected)} onValueChange={(v) => pick(Number(v))}>
              <SelectTrigger className="w-80 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {versions.data.versions.map((v) => (
                  <SelectItem key={v.number} value={String(v.number)}>
                    Version {v.number}
                    {v.live ? ' (live)' : ''} · {formatDateTime(v.created_at)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {dirty && <span className="text-xs text-amber-600">Unsaved changes</span>}
          <div className="ml-auto flex gap-2">
            {!isNew && !isLiveVersion && (
              <Button
                variant="outline"
                disabled={dirty || makeLive.isPending}
                title={dirty ? 'Save first: this makes the saved version live' : undefined}
                onClick={() => makeLive.mutate()}
              >
                {makeLive.isPending ? 'Making live…' : 'Make live'}
              </Button>
            )}
            {!isNew && (
              <Button
                variant="ghost"
                className="text-destructive"
                disabled={remove.isPending}
                onClick={() => {
                  if (window.confirm(`Delete ${doc.name}? Its steps stop running.`)) remove.mutate();
                }}
              >
                Delete
              </Button>
            )}
            {!isNew && (
              <Button variant="outline" disabled={dirty} title={dirty ? 'Save first: a test runs the saved agent' : undefined} onClick={() => setTesting(true)}>
                Test
              </Button>
            )}
            {(editing || dirty) && (
              <Button onClick={onSave} disabled={save.isPending || update.isPending || !doc.name.trim() || (!isNew && !dirty)}>
                {save.isPending || update.isPending ? 'Saving…' : isNew ? 'Create agent' : 'Save'}
              </Button>
            )}
          </div>
        </div>
        <Tabs value={tab} onValueChange={(v) => (isNew ? setNewTab(v) : navigate(teamPath(teamId!, v === 'general' ? undefined : v)))}>
          <TabsList>
            {TABS.map((t) => (
              <TabsTrigger key={t.key} value={t.key} disabled={isNew && t.key !== 'general' && t.key !== 'steps'}>
                {t.label}
                {t.key === 'runs' && !!waiting && <span className="ml-1.5 text-xs text-amber-600">{waiting} waiting</span>}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <div className="flex-1 min-h-0 overflow-auto">
        {tab === 'steps' ? (
          <StepsGraph doc={doc} setDoc={setDoc} connections={conns} hash={hash} />
        ) : (
          <div className="max-w-4xl p-6 space-y-6">
            {tab === 'general' && <General doc={doc} setDoc={setDoc} connections={conns} />}

            {tab === 'knowledge' && doc.id && <AgentKnowledge teamId={doc.id} steps={live?.members ?? []} />}
            {tab === 'permissions' && doc.id && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  This agent is one user in Security &amp; Governance: every step of it sees the data this allows. Set its group, and any
                  restrictions of its own.
                </p>
                <PrincipalPermissions principalId={agentUserId(doc.id)} name={live?.name ?? doc.name} />
              </div>
            )}
            {tab === 'runs' && doc.id && <AgentRuns teamId={doc.id} steps={live?.members ?? []} onWatch={(id) => setSearch({ watch: id })} />}
          </div>
        )}
      </div>
      {saved?.id && (
        <AgentTest
          teamId={saved.id}
          version={isLiveVersion ? undefined : selected!}
          steps={isLiveVersion ? (live?.members ?? []) : saved.members}
          connections={conns}
          open={testing}
          onOpenChange={setTesting}
          onStarted={(workItemId) => navigate(`${teamPath(saved.id!, 'runs')}?watch=${workItemId}`)}
        />
      )}
      {saved?.id && <WorkItemSheet teamId={saved.id} workItemId={watching} steps={live?.members ?? []} onClose={() => setSearch({})} />}
      <Dialog open={choosing} onOpenChange={setChoosing}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save version {selected}?</DialogTitle>
            <DialogDescription>
              Version {selected} isn't live. Save your changes as a new version, or update version {selected} in place. Updating it
              means its earlier test runs were of what it said before.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setChoosing(false)}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setChoosing(false);
                update.mutate(doc);
              }}
            >
              Update version {selected}
            </Button>
            <Button
              onClick={() => {
                setChoosing(false);
                save.mutate(doc);
              }}
            >
              Save as new version
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
